import { useRequiredParams } from '@pba/react-router';
import { Navigate, type NavigateProps, type Params, useLocation } from 'react-router';

type P<T> = {
  additionalSearch?: Record<string, string>;
  to: ((params: Readonly<[T] extends [string] ? Params<T> : T>) => NavigateProps['to']) | NavigateProps['to'];
} & Omit<NavigateProps, 'to'>;

const defaultAdditionalSearch = {};

const NavigateWithSearch = <T extends Record<string, string>>({
  additionalSearch = defaultAdditionalSearch,
  to,
  ...rest
}: P<T>) => {
  const { search } = useLocation();
  const params = useRequiredParams<T>();

  const toValue = typeof to === 'function' ? to(params) : to;
  const toObject = typeof toValue === 'string' ? { pathname: toValue } : toValue;

  const originalSearchParams = new URLSearchParams(search);
  const newSearchParams = new URLSearchParams(additionalSearch);

  const finalSearchParams = new URLSearchParams();

  for (const originalSearchParameter of originalSearchParams.entries()) {
    finalSearchParams.append(originalSearchParameter[0], originalSearchParameter[1]);
  }

  for (const newSearchParameter of newSearchParams.entries()) {
    finalSearchParams.append(newSearchParameter[0], newSearchParameter[1]);
  }

  return <Navigate replace to={{ ...toObject, search: finalSearchParams.toString() }} {...rest} />;
};

export default NavigateWithSearch;
